import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { matchPath, useNavigate, useLocation } from 'react-router-dom'
import {
  ContentSection,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from 'cs-kevlar-react'
import {
  SupportUserPermission,
  ApiTokenViewModel,
  WebAdminUser,
  Guid,
  ApiTokenConfigurationViewModel,
  CreateApiTokenViewModel,
  SupportUser,
  Scope,
} from '../../api'
import {
  useApiTokenApi,
  usePageNotifications,
  useAuth,
  useFormFactor,
} from 'hooks'
import {
  PageTitleRightActions,
  SidePanelContent,
  CreateToken,
  ResponsivePageActionButton,
  SupportUserPicker,
  UpgradeToken,
} from 'components'
import { getScopeLabel } from 'utils'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Add, Delete, Edit, Settings } from 'cs-kevlar-react/lib/es/icons'
import { AxiosResponse } from 'axios'
import { AsyncPayload } from 'components/base'
import { FormikFormValues } from 'components/apiTokens/CreateToken'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Api Tokens'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addTokenRoot: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(8),
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    pageTitleIconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
    token: {
      wordBreak: 'break-word',
      wordWrap: 'break-word',
    },
  }),
)

interface ApiTokenSecondaryTextProps {
  apiToken: ApiTokenViewModel
}

const ApiTokenSecondaryText: React.FunctionComponent<ApiTokenSecondaryTextProps> = (
  props,
) => {
  const expiresOn = new Date(props.apiToken.expiresOn)
  return (
    <Box>
      <Typography variant="body2" color="textSecondary">
        Token Id: {props.apiToken.apiTokenId}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Allowed Scopes:{' '}
        {(props.apiToken.scopes ?? []).map(getScopeLabel).join(', ')}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Expires On (UTC): {expiresOn.toLocaleString()}
      </Typography>
    </Box>
  )
}

const canGenerateToken = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(SupportUserPermission.ManageApiToken)
  )
}

const canViewToken = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(SupportUserPermission.ViewApiToken)
  )
}

const canManageAllTokens = (user: WebAdminUser | null) => {
  return (
    user !== null &&
    user.isSupport &&
    user.permissions &&
    user.permissions.includes(SupportUserPermission.ManageAllApiTokens)
  )
}

const canUpgradeToken = (user: WebAdminUser | null) => canManageAllTokens(user)

const API_TOKEN_BASE_PATH = '/apiTokens'
const ADD_API_TOKEN_PATH = '/apiTokens/new'
const MANAGE_API_TOKEN_PATH = '/apiTokens/manage'
const UPGRADE_API_TOKEN_PATH = `/apiTokens/:apiTokenId/upgrade`
const buildUpgradeTokenPath = (apiTokenId: string) => {
  return `/apiTokens/${apiTokenId}/upgrade`
}

const ApiToken: React.FunctionComponent = () => {
  const classes = useStyles({})
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const formFactor = useFormFactor()
  const {
    getApiTokens,
    createApiToken,
    revokeApiToken,
    getApiTokenConfiguration,
    upgradeApiToken,
  } = useApiTokenApi()
  const { dispatch: dispatchNotification } = usePageNotifications()
  const [apiTokens, setApiTokens] = React.useState<ApiTokenViewModel[] | null>(
    null,
  )
  const [config, setConfig] = React.useState<
    AsyncPayload<ApiTokenConfigurationViewModel>
  >({
    payload: null,
    isLoading: true,
  })
  const [
    currentSupportUser,
    setCurrentSupportUser,
  ] = React.useState<SupportUser | null>(null)

  usePageViewAudit(PageTitle, true, true)

  const loadConfiguration = async () => {
    const response = await getApiTokenConfiguration()
    if (response.status === 200) {
      setConfig({
        payload: response.data,
        isLoading: false,
      })
    }
  }

  const loadTokens = (
    func: () => Promise<AxiosResponse<ApiTokenViewModel[]>>,
  ) => {
    // empty out the existing state if we switched users
    setApiTokens(null)
    func()
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          setApiTokens(response.data)
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error loading the API token.',
          })
        }
      })
      .catch((error) => {
        console.error(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the API token.',
        })
      })
  }

  React.useEffect(() => {
    loadConfiguration()
    loadTokens(() =>
      getApiTokens(currentSupportUser === null ? null : currentSupportUser.id),
    )
  }, [currentSupportUser])

  const addToken = () => {
    navigate(ADD_API_TOKEN_PATH)
  }

  const manageTokens = () => {
    navigate(MANAGE_API_TOKEN_PATH)
  }

  const upgradeToken = (apiTokenId: Guid) => {
    navigate(buildUpgradeTokenPath(apiTokenId))
  }

  const deleteToken = (apiTokenId: Guid) => {
    revokeApiToken(
      currentSupportUser === null ? null : currentSupportUser.id,
      apiTokenId,
    ).then(
      (response) => {
        if (response.status === 204) {
          if (!apiTokens) return

          setApiTokens([
            ...apiTokens.filter(
              (apiToken) => apiToken.apiTokenId !== apiTokenId,
            ),
          ])
        }
      },
      () => {
        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error removing the API token.',
        })
      },
    )
  }

  const upgradeTokenScopesViaApi = async (
    apiTokenId: Guid,
    newScopes: Scope[],
  ) => {
    try {
      if (apiTokens === null) return

      const response = await upgradeApiToken(
        apiTokenId,
        currentSupportUser === null ? null : currentSupportUser.id,
        {
          scopes: newScopes,
        },
      )

      if (response.status === 200) {
        const newApiTokens = apiTokens.map((apiToken) => {
          if (apiToken.apiTokenId === apiTokenId) return response.data

          return {
            ...apiToken,
          }
        })
        setApiTokens(newApiTokens)

        navigate(API_TOKEN_BASE_PATH)
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error upgrading the API token scopes.',
      })
    }
  }

  const createApiTokenViaApi = async (formik: FormikFormValues) => {
    try {
      if (formik.expiresOn === null) return

      const data: CreateApiTokenViewModel = {
        expiresOn: formik.expiresOn.toISOString(),
      }
      const response = await createApiToken(
        currentSupportUser === null ? null : currentSupportUser.id,
        data,
      )
      if (response.status === 201) {
        setApiTokens([...(apiTokens ?? []), response.data])
        dispatchNotification({
          type: 'SetSuccess',
          payload: 'API token created.',
        })
        navigate(API_TOKEN_BASE_PATH)
        return
      }

      throw 'Unsuccessful token creation'
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error creating the API token.',
      })
    }
  }

  const onSelectSupportUser = (supportUser: SupportUser | null) => {
    // if they chose themselves then null it out
    if (supportUser !== null && supportUser.username !== user?.name) {
      setCurrentSupportUser(supportUser)
      return
    }

    setCurrentSupportUser(null)
  }

  const addApiTokenMatch = matchPath({
    path: ADD_API_TOKEN_PATH,
    caseSensitive: true,
  }, location.pathname)

  const isAddingApiToken = addApiTokenMatch !== null

  const upgradeApiTokenMatch = matchPath({
    path: UPGRADE_API_TOKEN_PATH,
    caseSensitive: true,
  }, location.pathname)

  const isUpgradingApiToken = upgradeApiTokenMatch !== null

  const manageApiTokenMatch = matchPath({
    path: MANAGE_API_TOKEN_PATH,
    caseSensitive: true,
  }, location.pathname)
  
  const isManagingApiToken = manageApiTokenMatch !== null

  let editingApiTokenId: string | null = null
  if (isUpgradingApiToken && upgradeApiTokenMatch !== null) {
    editingApiTokenId = (upgradeApiTokenMatch.params as any).apiTokenId
  }

  const isSidePanelOpen =
    isManagingApiToken || isUpgradingApiToken || isAddingApiToken

  const title =
    currentSupportUser === null
      ? 'Api Tokens'
      : `Api Tokens: ${currentSupportUser.username}`

  return (
    <React.Fragment>
      <PageTitleRightActions>
        {canManageAllTokens(user) && (
          <ResponsivePageActionButton
            text="Manage API Tokens"
            onClick={manageTokens}
            color="primary"
          >
            <Settings />
          </ResponsivePageActionButton>
        )}
        {canGenerateToken(user) && (
          <ResponsivePageActionButton
            text="Add New API Token"
            onClick={addToken}
            color="primary"
            disabled={config.payload === null}
            phoneClassName={classes.pageTitleIconButton}
          >
            <Add />
          </ResponsivePageActionButton>
        )}
      </PageTitleRightActions>
      <ContentSection title={title} disableContentPadding>
        <List dense disablePadding>
          {apiTokens !== null &&
            apiTokens.map((apiToken) => (
              <ListItem
                key={apiToken.apiTokenId}
                selected={editingApiTokenId === apiToken.apiTokenId}
              >
                <ListItemText
                  primary={
                    <Typography className={classes.token}>
                      {apiToken.token}
                    </Typography>
                  }
                  disableTypography
                  secondary={<ApiTokenSecondaryText apiToken={apiToken} />}
                />

                <ListItemSecondaryAction>
                  {!isSidePanelOpen && canUpgradeToken(user) && (
                    <IconButton
                      edge="end"
                      aria-label="upgrade"
                      onClick={() => upgradeToken(apiToken.apiTokenId)}
                      size="large">
                      <Edit />
                    </IconButton>
                  )}
                  {!isSidePanelOpen && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => deleteToken(apiToken.apiTokenId)}
                      size="large">
                      <Delete />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </ContentSection>
      {isAddingApiToken && (
        <SidePanelContent
          anchor="right"
          title="Add API Token"
          onClose={() => navigate(API_TOKEN_BASE_PATH)}
        >
          <CreateToken
            onClose={() => navigate(API_TOKEN_BASE_PATH)}
            apiTokenConfig={config.payload}
            onCreate={createApiTokenViaApi}
          />
        </SidePanelContent>
      )}
      {isUpgradingApiToken && editingApiTokenId !== null && apiTokens !== null && (
        <SidePanelContent
          anchor="right"
          title="Upgrade API Token Scopes"
          onClose={() => navigate(API_TOKEN_BASE_PATH)}
        >
          <UpgradeToken
            apiToken={
              apiTokens.find(
                (x) => x.apiTokenId === editingApiTokenId,
              ) as ApiTokenViewModel
            }
            onClose={() => navigate(API_TOKEN_BASE_PATH)}
            onSubmit={upgradeTokenScopesViaApi}
          />
        </SidePanelContent>
      )}
      {isManagingApiToken && (
        <SidePanelContent
          anchor="right"
          title="Manage API Tokens"
          onClose={() => navigate(API_TOKEN_BASE_PATH)}
        >
          <SupportUserPicker
            initialSupportUserId={
              currentSupportUser !== null ? currentSupportUser.id : null
            }
            onClose={() => navigate(API_TOKEN_BASE_PATH)}
            onSelect={onSelectSupportUser}
          />
        </SidePanelContent>
      )}
    </React.Fragment>
  );
}

export const ApiTokenPage: PageDefinition = {
  routes: () => [
    API_TOKEN_BASE_PATH,
    ADD_API_TOKEN_PATH,
    MANAGE_API_TOKEN_PATH,
    UPGRADE_API_TOKEN_PATH,
  ],
  type: [PageType.Support],
  sortOrder: () => 5,
  component: <ApiToken />,
  navLinkText: PageTitle,
  pageTitle: PageTitle,
  canAccess: (user) => {
    return canGenerateToken(user) || canViewToken(user)
  },
  buildNavLink: (user) => {
    return '/apiTokens'
  },
}
